/**
 * One labelled value somebody has to get out of this screen and into somebody
 * else's console, with a copy button that is an accelerant rather than the only
 * way out.
 *
 * ## Why this is a file rather than a function in the panel that first needed it
 *
 * It was written once, inside `RedirectDomainPanel`, as `RecordField` — the
 * four rows of a CNAME record. Slice 11 then produced a second place with
 * exactly the same job: the SNS topic panel has to hand over a webhook URL that
 * gets pasted into an AWS console, and it is a long opaque string with a token
 * in it, which is the worst possible thing to ask somebody to retype.
 *
 * The alternative was to leave `RecordField` where it was and write a second
 * copy beside it. Rejected for the reason `request-failure.tsx` gives about the
 * same screen a few files over: the failure modes here are subtle — a clipboard
 * the browser refuses, an announcement a screen reader misses — and they were
 * got right once, at some cost. A second copy is where a fix lands in one and
 * not the other, and the symptom is somebody pasting the *previous* contents of
 * their clipboard into a DNS record or an event destination and not finding out
 * for days.
 *
 * Nothing about the behaviour changed in the move. This is `RecordField`'s
 * markup and logic verbatim, with the label width lifted to a prop because the
 * two callers have different label lengths and a fixed `w-36` made the second
 * one wrap.
 *
 * ## The copy button is never the only way to get the value
 *
 * `navigator.clipboard` is unavailable in a non-secure context and can be
 * refused outright by permission policy, so a copy can fail for reasons that
 * have nothing to do with this app and nothing the person can fix. The value is
 * therefore always rendered in full, selectable, in a monospace face, and a
 * failed copy **says so**. A copy control whose failure mode is silence is
 * worse than no copy control at all: the person walks away believing they have
 * the value and pastes whatever was in the clipboard before.
 *
 * That is also why the failure text names the browser rather than apologising
 * in the abstract — "your browser would not let Wend use the clipboard" tells
 * somebody the value on screen is still correct and still theirs to select,
 * which is the one thing they need to know to keep going.
 */
import { Button } from '@wend/ui';
import { Check, Copy } from 'lucide-react';
import { useState } from 'react';

export interface CopyableValueProps {
  /** What the value is, in the words the destination console uses for it. */
  readonly label: string;
  /** The value itself. Always rendered in full — never truncated or elided. */
  readonly value: string;
  /**
   * Whether to offer the button. False for values nobody needs on a clipboard
   * — a record type of `CNAME`, a TTL of `300` — where a copy control would be
   * three more things to tab through for no gain.
   */
  readonly copyable?: boolean;
  /** Tailwind width for the label column. Widened by callers with long labels. */
  readonly labelWidth?: string;
}

export function CopyableValue({
  label,
  value,
  copyable = true,
  labelWidth = 'w-36',
}: CopyableValueProps) {
  const [copied, setCopied] = useState<'ok' | 'failed' | null>(null);

  async function copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(value);
      setCopied('ok');
    } catch {
      // Not rethrown and not logged: a clipboard the browser will not hand over
      // is not a fault of this app, of the tenant's DNS, or of their AWS
      // account, and the value is on screen and selectable regardless.
      setCopied('failed');
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-line py-2.5 first:border-t-0 first:pt-0">
      <dt
        className={`${labelWidth} shrink-0 font-mono text-[0.66rem] uppercase tracking-[0.14em] text-muted`}
      >
        {label}
      </dt>
      <dd className="min-w-0 flex-1 break-all font-mono text-[0.85rem] text-text">{value}</dd>
      {copyable && (
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            // A stable accessible name. The visible label swaps to "Copied",
            // which a sighted reader takes as the feedback; a screen reader
            // gets the same feedback from the live region beside it instead,
            // because a control whose *name* changes under the cursor is worse
            // to navigate than one whose name is boring.
            aria-label={`Copy the ${label.toLowerCase()}`}
            onClick={() => {
              void copy();
            }}
          >
            {copied === 'ok' ? (
              <Check aria-hidden="true" className="size-4" />
            ) : (
              <Copy aria-hidden="true" className="size-4" />
            )}
            {copied === 'ok' ? 'Copied' : 'Copy'}
          </Button>

          {/*
            Kept mounted so the announcement is reliable — a live region
            inserted with its text already in it is announced unpredictably,
            `role="status"` especially so. Failure is said out loud rather than
            swallowed; see the file header.
          */}
          <span role="status" className="text-[0.8rem] text-warn">
            {copied === 'failed'
              ? 'Your browser would not let Wend use the clipboard — select the value and copy it.'
              : copied === 'ok'
                ? 'Copied.'
                : ''}
          </span>
        </div>
      )}
    </div>
  );
}
